import React, { useEffect, useRef, useState } from "react";
import {
    Avatar, Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    LinearProgress, Paper, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ActionButton from "../../../component/common/ActionButton";
import { channelApi } from "../api/channelApi";
import { notify } from "../../../util/toast";
import { applyRangeEndChange, applyRangeStartChange } from "../../../util/dateUtils";
import FormField from "../../../component/common/FormField";
import LoadingDots from "../../../component/common/LoadingDots";
import AppAutocomplete from "../../../component/common/AppAutocomplete";
import ComboBox from "../../../component/common/ComboBox";
import { getApiErrorMessage } from "../../../util/apiFeedback";
import { STATUS_LABELS, SELECTABLE_STATUSES, StatusChip } from "./taskShared";
import TaskDetailModal from "./TaskDetailModal";

const STATUS_OPTIONS = SELECTABLE_STATUSES.map(k => ({ value: k, label: STATUS_LABELS[k].label }));

function TaskRow({ task, myId, onOpen }) {
    const mineCreator = task.createdBy?.id === myId;
    const mineAssignee = task.assignee?.id === myId;
    return (
        <Paper
            onClick={() => onOpen(task)}
            sx={{
                p: 1.5, borderRadius: 1.5, border: "1px solid rgba(0,0,0,0.07)", cursor: "pointer",
                transition: "all .12s",
                "&:hover": { borderColor: "#2563eb55", boxShadow: "0 2px 10px rgba(37,99,235,0.08)" },
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.5}>
                <StatusChip status={task.status} />
                <Box flex={1} minWidth={0}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography noWrap sx={{ fontWeight: 600, fontSize: 14 }}>{task.title}</Typography>
                        {mineAssignee && (
                            <Chip label="내 담당" size="small"
                                sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#eff6ff", color: "#2563eb" }} />
                        )}
                        {mineCreator && (
                            <Chip label="내 지시" size="small"
                                sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#f5f3ff", color: "#7c3aed" }} />
                        )}
                    </Stack>
                    {task.description && (
                        <Typography noWrap sx={{ fontSize: 12, color: "text.secondary", mt: 0.2 }}>{task.description}</Typography>
                    )}
                    {(task.startDate || task.endDate) && (
                        <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.3 }}>
                            {task.startDate || "미정"} ~ {task.endDate || "미정"}
                        </Typography>
                    )}
                </Box>
                {task.assignee && (
                    <Tooltip title={task.assignee.name}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: "#2563eb" }}>
                            {task.assignee.name?.[0]}
                        </Avatar>
                    </Tooltip>
                )}
                <ChevronRightRoundedIcon sx={{ color: "#cbd5e1" }} />
            </Stack>
        </Paper>
    );
}

/** 진행률 카드의 테두리·구분선 — 두 곳에서 쓰여 상수로 뽑았다. */
const PROGRESS_BORDER = "rgba(0,0,0,0.07)";

export default function ProjectTaskTab({ channelId, members, myId, onChannelRefresh,
                                        /** 조회 범위 — {} (전체) | { folderId } | { onlyUnfiled: true } */
                                        folderScope = {},
                                        /** 새 업무의 기본 폴더 — '전체'·'미분류' 를 보고 있으면 null */
                                        composeFolderId = null }) {
    /**
     * 폴더 범위를 useEffect 의존성으로 쓰기 위한 문자열 키 —
     * folderScope 는 매 렌더 새 객체라 그대로 넣으면 무한 재조회가 된다.
     */
    const folderKey = JSON.stringify(folderScope);

    const [tasks, setTasks] = useState([]);
    /** { total, done, byFolder: [{ folderId, folderName, total, done }] } */
    const [progress, setProgress] = useState({ total: 0, done: 0, byFolder: [] });
    const [loading, setLoading] = useState(true);

    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const selectedTask = tasks.find(t => t.id === selectedTaskId) || null;

    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        title: "", description: "", assigneeId: "",
        status: "STARTED", startDate: "", endDate: "",
    });
    const [pendingFiles, setPendingFiles] = useState([]);
    const fileInputRef = useRef();

    const load = async () => {
        setLoading(true);
        try {
            const [tasksRes, progressRes] = await Promise.all([
                channelApi.getTasks(channelId, folderScope),
                // 진행률은 <b>채널 전체</b> 기준을 유지한다 — 폴더별 진행률로 바꾸면
                // 상단 숫자가 칩을 누를 때마다 달라져 "이 프로젝트가 얼마나 진행됐나" 를 잃는다.
                channelApi.getProgress(channelId),
            ]);
            setTasks(Array.isArray(tasksRes) ? tasksRes : (tasksRes?.tasks ?? []));
            setProgress(progressRes ?? {});
        } catch {
            notify("업무 목록 불러오기 실패", "error");
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [channelId, folderKey]);

    const openDetail = (task) => { setSelectedTaskId(task.id); setDetailOpen(true); };

    const handleCreate = async () => {
        if (!form.title.trim()) { notify("업무명을 입력하세요", "error"); return; }
        try {
            const res = await channelApi.createTask(channelId, {
                title: form.title,
                description: form.description || null,
                assigneeId: form.assigneeId || null,
                status: form.status,
                startDate: form.startDate || null,
                endDate: form.endDate || null,
                folderId: composeFolderId ?? null,
            });
            const taskId = res.id;
            // 파일 업로드
            for (const f of pendingFiles) {
                await channelApi.uploadFile(channelId, f, null, null, taskId);
            }
            // 일정 자동 연동: 시작일 또는 마감일이 있으면 일정 자동 등록
            if (form.startDate || form.endDate) {
                try {
                    await channelApi.createSchedule(channelId, {
                        // 업무와 같은 폴더로 — 다른 폴더에 들어가면 업무와 그 일정이 서로 안 보인다.
                        folderId: composeFolderId ?? null,
                        title: form.title,
                        description: form.description || null,
                        startAt: form.startDate ? `${form.startDate}T09:00:00` : null,
                        endAt: form.endDate ? `${form.endDate}T18:00:00` : null,
                        taskId: taskId,
                    });
                } catch { /* 일정 자동 등록 실패는 무시 */ }
            }
            setOpen(false);
            setForm({ title: "", description: "", assigneeId: "", status: "STARTED", startDate: "", endDate: "" });
            setPendingFiles([]);
            load();
            onChannelRefresh?.();
            notify("업무가 추가되었습니다", "success");
        } catch (e) {
            notify(getApiErrorMessage(e, "업무 추가 실패"), "error");
        }
    };

    // 상세 모달에서 상태·설정·삭제 변경 후 목록/전체 갱신.
    const handleChanged = async () => {
        await load();
        onChannelRefresh?.();
    };

    const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

    /**
     * 폴더별 진행률 — 전체 아래에 함께 보여준다.
     *
     * <p>전체 숫자를 폴더별로 갈아치우지 않는 이유: 칩을 누를 때마다 상단 숫자가 달라지면
     * "이 프로젝트가 얼마나 진행됐나" 를 잃는다. 대신 둘을 나란히 두고, 지금 보고 있는 폴더 줄만
     * 강조한다. 폴더를 안 쓰는 채널(미분류 한 줄뿐)에서는 이 블록을 아예 감춘다.
     */
    const folderRows = Array.isArray(progress.byFolder) ? progress.byFolder : [];
    const showFolderBreakdown = folderRows.some(r => r.folderId != null);
    const currentFolderId = folderScope.folderId ?? null;

    return (
        <Box>
            {/* 진행률 */}
            <Paper sx={{ p: 2, borderRadius: 1.5, mb: 2, border: `1px solid ${PROGRESS_BORDER}` }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>전체 진행률</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#2563eb" }}>{pct}%</Typography>
                </Stack>
                <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                        height: 8, borderRadius: 4,
                        bgcolor: "rgba(37,99,235,0.10)",
                        "& .MuiLinearProgress-bar": { bgcolor: "#2563eb", borderRadius: 4 },
                    }}
                />
                <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                    {progress.done} / {progress.total} 완료
                </Typography>

                {showFolderBreakdown && (
                    <Box sx={{ mt: 1.75, pt: 1.5, borderTop: `1px solid ${PROGRESS_BORDER}` }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", mb: 1 }}>
                            폴더별 진행률
                        </Typography>
                        <Stack spacing={1}>
                            {folderRows.map(row => {
                                const rowPct = row.total > 0 ? Math.round((row.done / row.total) * 100) : 0;
                                // 지금 보고 있는 폴더 줄을 굵게 — 칩 선택과 이 표를 눈으로 잇는 장치.
                                const active = (row.folderId ?? null) === currentFolderId;
                                return (
                                    <Box key={row.folderId ?? "unfiled"}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <Typography noWrap sx={{
                                                fontSize: 12.5, minWidth: 0,
                                                fontWeight: active ? 700 : 500,
                                                color: active ? "#0f172a" : "#475569",
                                            }}>
                                                {row.folderName}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: 12, flexShrink: 0, ml: 1,
                                                fontWeight: active ? 700 : 500,
                                                color: active ? "#2563eb" : "#94a3b8",
                                            }}>
                                                {rowPct}% · {row.done}/{row.total}
                                            </Typography>
                                        </Stack>
                                        <LinearProgress
                                            variant="determinate" value={rowPct}
                                            sx={{
                                                mt: 0.4, height: active ? 6 : 4, borderRadius: 3,
                                                bgcolor: "rgba(37,99,235,0.08)",
                                                "& .MuiLinearProgress-bar": {
                                                    bgcolor: active ? "#2563eb" : "#93c5fd", borderRadius: 3,
                                                },
                                            }}
                                        />
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                )}
            </Paper>

            {/* 업무 목록 헤더 */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography fontWeight={700}>업무 현황</Typography>
                <ActionButton variant="add" startIcon={<AddRoundedIcon />}
                    onClick={() => setOpen(true)}
                    label="업무 추가" />
            </Stack>

            {loading ? (
                <LoadingDots />
            ) : tasks.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    <Typography>등록된 업무가 없습니다.</Typography>
                </Box>
            ) : (
                <Stack spacing={1.5}>
                    {tasks.map(task => (
                        <TaskRow key={task.id} task={task} myId={myId} onOpen={openDetail} />
                    ))}
                </Stack>
            )}

            {/* 업무 상세 모달 — 지시/피드백 워크플로우 */}
            <TaskDetailModal
                open={detailOpen && !!selectedTask}
                onClose={() => setDetailOpen(false)}
                task={selectedTask}
                channelId={channelId}
                members={members}
                myId={myId}
                onChanged={handleChanged}
            />

            {/* 업무 추가 다이얼로그 */}
            <Dialog open={open} onClose={() => { setOpen(false); setPendingFiles([]); }} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>새 업무 추가</DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Stack spacing={2}>
                        <FormField label="업무명" required>
                            <TextField size="small" value={form.title}
                                onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                                placeholder="업무명을 입력하세요" fullWidth />
                        </FormField>
                        <Stack direction="row" spacing={1}>
                            <FormField label="상태" required sx={{ flex: 1 }}>
                                <ComboBox clearable={false}
                                    options={STATUS_OPTIONS}
                                    value={form.status}
                                    onChange={v => setForm(p => ({ ...p, status: v }))} />
                            </FormField>
                            <FormField label="담당자" sx={{ flex: 1 }}>
                                <AppAutocomplete
                                    placeholder="이름·부서 검색"
                                    options={(members || []).map(m => m.employee).filter(Boolean)}
                                    value={(members || []).map(m => m.employee).find(e => e?.id === Number(form.assigneeId)) ?? null}
                                    onChange={(v) => setForm(p => ({ ...p, assigneeId: v?.id ?? "" }))}
                                    getOptionLabel={(o) => o?.name || ""}
                                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                    secondaryText={(o) => [o?.deptName, o?.positionName].filter(Boolean).join(" · ")}
                                />
                            </FormField>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <FormField label="시작일" sx={{ flex: 1 }}>
                                <TextField type="date" size="small" value={form.startDate}
                                    onChange={e => setForm(p => applyRangeStartChange(p, e.target.value, { startKey: "startDate", endKey: "endDate", isDate: true }))} />
                            </FormField>
                            <FormField label="마감일" sx={{ flex: 1 }}>
                                <TextField type="date" size="small" value={form.endDate}
                                    onChange={e => setForm(p => applyRangeEndChange(p, e.target.value, { startKey: "startDate", endKey: "endDate" }))} />
                            </FormField>
                        </Stack>
                        <FormField label="설명">
                            <TextField size="small" value={form.description}
                                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                                placeholder="설명을 입력하세요" multiline rows={2} fullWidth />
                        </FormField>
                        {/* 파일 첨부 */}
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1} mb={pendingFiles.length > 0 ? 0.75 : 0}>
                                <ActionButton variant="secondary"
                                    startIcon={<AttachFileRoundedIcon sx={{ fontSize: 15 }} />}
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{
                                        color: "#64748b", borderColor: "transparent", fontSize: 12, py: 0.5,
                                        "&:hover": { borderColor: "transparent", bgcolor: "#f1f5f9" },
                                    }}
                                    label="파일 첨부" />
                                <input ref={fileInputRef} type="file" hidden multiple
                                    onChange={e => setPendingFiles(p => [...p, ...Array.from(e.target.files)])} />
                            </Stack>
                            {pendingFiles.length > 0 && (
                                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                    {pendingFiles.map((f, i) => (
                                        <Chip key={i}
                                            icon={<InsertDriveFileRoundedIcon sx={{ fontSize: 13 }} />}
                                            label={f.name} size="small"
                                            onDelete={() => setPendingFiles(p => p.filter((_, j) => j !== i))}
                                            sx={{ maxWidth: 220, fontSize: 11, bgcolor: "#f1f5f9", border: "1px solid #e2e8f0" }} />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <ActionButton variant="cancel" onClick={() => { setOpen(false); setPendingFiles([]); }} />
                    <ActionButton variant="add" onClick={handleCreate} />
                </DialogActions>
            </Dialog>
        </Box>
    );
}
